import { apiQueryKey } from '@repo/api-codegen';
import { trackEvent } from '@repo/common/analytics';
import { EntityType } from '@repo/common/enums/entityType';
import { useMount } from 'ahooks';
import { observer } from 'mobx-react-lite';
import { useCallback, useMemo } from 'react';
import { useParams } from 'react-router';
import {
	queryClient,
	useAuthUser,
	useQuestionsEnabled,
	useSuspenseFeatureAccess,
	useUpdateSecodaEntity,
} from '../../api';
import useSuspenseSecodaEntity from '../../api/hooks/secodaEntity/useSuspenseSecodaEntity';
import Documentation from '../../components/Documentation';
import EntityDiscussions from '../../components/EntityDiscussions';
import { getEntityPageSidebarProps } from '../../components/EntityDrawer/utils';
import EntityLogo from '../../components/EntityLogo/EntityLogo';
import EntityPageLayout from '../../components/EntityPageLayout';
import EntityPageLayoutSkeleton from '../../components/EntityPageLayout/EntityPageLayoutSkeleton';
import { useLineageTab } from '../../components/EntityPageLayout/hooks';
import { getDefaultTab } from '../../components/EntityPageLayout/utils';
import { LineageTab } from '../../components/LineageGraph/LineageTab';
import OverviewPageTabs from '../../components/Overview/OverviewPageTabs';
import { useTabNavigate } from '../../utils/hook/useTabNavigate';
import { useParamsIdSuffixUuid } from '../../utils/hook/utils';
import type { DjangoValueType } from '../TemplatePage/types';

const InnerGenericPage = observer(({ id }: { id: string }) => {
	const { user, workspace } = useAuthUser();
	const { lineageAccess, secodaCoreAccess } = useSuspenseFeatureAccess();
	const questionsEnabled = useQuestionsEnabled();
	const navigateToTab = useTabNavigate();

	const { data: genericEntity } = useSuspenseSecodaEntity({
		id,
	});

	useMount(() => {
		trackEvent('generic_entity/open', {}, user, workspace);
	});

	const { mutateAsync } = useUpdateSecodaEntity({
		options: {
			onSuccess: () =>
				queryClient.invalidateQueries({
					queryKey: apiQueryKey('quality'),
				}),
		},
	});

	const updateGenericEntity = useCallback(
		async (key: string, value: DjangoValueType, saveRemotely = true) => {
			if (saveRemotely) {
				await mutateAsync({
					data: {
						id: genericEntity!.id,
						[key]: value,
					},
				});
				trackEvent('generic/properties/update', {}, user, workspace!);
			}
		},
		[mutateAsync, genericEntity, user, workspace]
	);

	const handleTabChange = useCallback(
		(updatedTab: string) => {
			if (updatedTab === 'lineage') {
				trackEvent('table/lineage/open', {}, user, workspace);
			}
			navigateToTab(updatedTab);
		},
		[navigateToTab, user, workspace]
	);

	const { lineageFlowStore, lineageFlowUIStore, lineageFilterStore } =
		useLineageTab(
			genericEntity.id,
			genericEntity.published,
			EntityType.generic,
			genericEntity.native_type
		);

	if (!genericEntity) {
		return <EntityPageLayoutSkeleton />;
	}

	const tabs = useMemo(
		() => [
			{
				value: 'lineage',
				label: 'Lineage',
				condition: !!lineageAccess,
				component: (
					<LineageTab
						lineageFlowStore={lineageFlowStore}
						lineageFlowUIStore={lineageFlowUIStore}
						lineageFilterStore={lineageFilterStore}
					/>
				),
			},
			{
				value: 'discussions',
				label: 'Questions',
				condition: !!secodaCoreAccess && questionsEnabled,
				component: <EntityDiscussions entity={genericEntity} />,
			},
			{
				value: 'documentation',
				label: 'Documentation',
				component: <Documentation entity={genericEntity} />,
			},
		],
		[genericEntity, lineageAccess, secodaCoreAccess, questionsEnabled]
	);

	const { tab: urlTabParam } = useParams();

	const defaultTab = useMemo(() => getDefaultTab(tabs), [tabs]);
	const currentTab = urlTabParam ?? defaultTab;

	return (
		<EntityPageLayout
			key={genericEntity.id}
			name="Generic"
			isReadOnlyTitle
			entity={genericEntity}
			icon={<EntityLogo entity={genericEntity} size={32} />}
			updateEntity={updateGenericEntity}
			withDescription
			withEntityByteSize
			withEntityRowCount
			{...getEntityPageSidebarProps(EntityType.generic)}
		>
			<OverviewPageTabs
				key={genericEntity.id}
				defaultTab={defaultTab}
				currentTab={currentTab}
				tabs={tabs}
				onTabChange={handleTabChange}
			/>
		</EntityPageLayout>
	);
});

// by memoizing the inner component, we can avoid re-rendering the entire page on tab changes and only re-render the tab panel
export default function GenericPage() {
	const id = useParamsIdSuffixUuid();

	return <InnerGenericPage id={id} />;
}
